import {
  Shimmer,
  toBase64,
} from '@we-make-websites/ui-lib/src/components/utility/imageShimmer/ImageShimmer';
import Image from 'next/image';
import { useRef } from 'react';
import type { StoreMediaNode } from '../productContext/types/storeMedia';
import { StoreMediaType } from '../productContext/types/storeMedia';

export const MediaItem = ({
  media,
  active,
  imageProps = {
    sizes: '(min-width: 1024px) 511px, (min-width: 576px) 800px, 300px',
    fill: true,
  },
}: {
  media: StoreMediaNode;
  active: boolean;
  imageProps?: {
    height?: number;
    width?: number;
    style?: Record<string, string>;
    sizes?: string;
    fill?: boolean;
  };
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  /**
   * Pause the video once the media becomes inactive.
   * - `useEffect` is used to prevent the video from playing when the
   *    component is re-rendered and the `active` prop has not changed.
   */
  if (active) {
    if (videoRef.current) videoRef.current.play();
  } else {
    if (videoRef.current) videoRef.current.pause();
  }

  switch (media.type) {
    case StoreMediaType.Image:
      return (
        <Image
          src={media.image.url}
          alt={media.image.altText}
          title={media.image.altText}
          placeholder="blur"
          blurDataURL={`data:image/svg+xml;base64,${toBase64(
            Shimmer(media.image.width, media.image.height)
          )}`}
          {...imageProps}
        />
      );

    case StoreMediaType.Video:
      return (
        // Video caption error suppressed for dummy videos
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
          ref={videoRef}
          controls
          playsInline
          poster={media.video.previewImage ?? undefined}
        >
          {media.video.sources.map((source) => (
            <source key={source.url} src={source.url} type={source.mimeType} />
          ))}
        </video>
      );
    default:
      return <div>Unsupported Media</div>;
  }
};
